/**
 * Helpers de presentación (fecha relativa + tamaño de archivo).
 *
 * La fecha relativa usa Intl.RelativeTimeFormat con el locale activo del i18n,
 * para que "hace 5 minutos" / "há 5 minutos" salga en el idioma correcto.
 */
import { getLocale } from './i18n';

const localeTag: Record<string, string> = { es: 'es', pt: 'pt-BR' };

/** "hace un momento", "hace 5 min", "hace 2 h", "ayer", fecha absoluta si es lejano. */
export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;

  const tag = localeTag[getLocale()] ?? 'es';
  const diffSec = Math.round((then - Date.now()) / 1000); // negativo = pasado
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });

  if (abs < 45) return rtf.format(0, 'second').replace('0', '').trim() || nowLabel(tag);
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 7 * 86_400) return rtf.format(Math.round(diffSec / 86_400), 'day');

  // Lejano: fecha absoluta corta.
  return new Intl.DateTimeFormat(tag, { day: '2-digit', month: 'short', year: 'numeric' }).format(
    then,
  );
}

function nowLabel(tag: string): string {
  return tag.startsWith('pt') ? 'agora' : 'recién';
}

/** Tamaño legible: 1.4 MB, 320 KB, 12 B. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}
